import 'range-slider-wc';

import {debounce} from '../utils/debounce';
import {filterSearchParams} from '../utils/search-params';
import initPagination from './pagination';
import {initWishlistCallback} from '../utils/wishlist';

const selectors = {
  resultsNavbar: '[data-results-navbar]',
  resultsGrid: '[data-results-grid]',
  pagination: '[data-results-pagination]',
  filterToggle: '[data-results-filter-toggle]',
  filterDrawer: '[data-results-filter-drawer]',
  filterContent: '[data-results-filter-content]',
  filterClearLink: '[data-results-filter-clear-link]',
  filterPriceRangeSlider: '[data-results-filter-price-range-slider]',
  filterPriceRangeMin: '[data-results-filter-price-range-min]',
  filterPriceRangeMax: '[data-results-filter-price-range-max]',
  filterFooter: '[data-results-filter-footer]',
  filterTopbar: '[data-results-filter-topbar]',
  layoutSwitchButton: '[data-results-layout-switch]',
  sortByToggle: '[data-sort-toggle]',
  sortByModal: '[data-sort-modal]',
  resultsCount: '[data-results-count]',
};

const classes = {
  isHidden: 'is-hidden',
  isVisible: 'is-visible',
  isSelected: 'is-selected',
  layoutSwitchSelected: 'layout-switch__btn--selected',
  filterIsVisible: 'filter-is-visible',
  sortByActive: 'sort__modal--active',
  bodySortActive: 'sort-is-active',
};

// Utility function to deduplicate filter parameters in form data
const deduplicateFormData = (formData) => {
  // Create maps to store unique values by parameter name
  const filterParams = new Map();
  const otherParams = new Map();

  // Organize all form data, grouping filter parameters
  for (const [key, value] of formData.entries()) {
    if (key.includes('filter.')) {
      // For price range parameters, only keep the latest value
      if (key.includes('filter.v.price.gte') || key.includes('filter.v.price.lte')) {
        filterParams.set(key, value); // Overwrite with latest value
      } else {
        // For other filter parameters, accumulate unique values
        if (!filterParams.has(key)) {
          filterParams.set(key, new Set());
        }
        filterParams.get(key).add(value);
      }
    } else {
      // For non-filter parameters, just store the latest value
      otherParams.set(key, value);
    }
  }

  // Create a new FormData object with deduplicated values
  const deduplicatedFormData = new FormData();

  // Add all non-filter parameters
  for (const [key, value] of otherParams.entries()) {
    deduplicatedFormData.append(key, value);
  }

  // Add deduplicated filter parameters
  for (const [key, valueOrSet] of filterParams.entries()) {
    if (key.includes('filter.v.price.gte') || key.includes('filter.v.price.lte')) {
      // For price parameters, add the single latest value
      deduplicatedFormData.append(key, valueOrSet);
    } else {
      // For other filter parameters, add all unique values from the Set
      for (const value of valueOrSet) {
        deduplicatedFormData.append(key, value);
      }
    }
  }

  return deduplicatedFormData;
};

const ResultsFilterSort = (container, id) => {
  const resultsNavbar = container.querySelector(selectors.resultsNavbar);
  const resultsGrid = container.querySelector(selectors.resultsGrid);
  let selectedLayoutView = window.sessionStorage.getItem('resultsLayoutView') || 'default';
  const defaultSortBy = container.dataset.defaultSortBy || 'manual';

  // Store range slider state globally to persist across re-renders
  let rangeSliderState = {
    valueMin: null,
    valueMax: null,
    isUpdating: false,
  };

  initNavbar();
  initPagination(id);
  initWishlistCallback(selectors.resultsGrid);

  // Initialize dropdown functionality once
  initNavbarFilterDropdowns();

  // Start observing for DOM changes that might affect dropdowns
  observeDropdownChanges();

  // Add a periodic check to ensure dropdowns remain functional
  // This helps recover from any interference by other JavaScript
  setInterval(() => {
    reinitializeDropdownsIfNeeded();
  }, 2000);

  function initNavbar() {
    if (!resultsNavbar || !resultsGrid) return;

    const filterDrawer = container.querySelector(selectors.filterDrawer);
    const filterContent = container.querySelectorAll(selectors.filterContent);
    const filterFooter = container.querySelector(selectors.filterFooter);
    const filterTopbar = container.querySelector(selectors.filterTopbar);
    const pagination = container.querySelectorAll(selectors.pagination);
    const sortByToggle = container.querySelector(selectors.sortByToggle);
    const sortByModal = container.querySelector(selectors.sortByModal);
    let sortByIsExpanded = false;

    const renderPage = async (searchParams, currentFilter = false) => {
      const url = `${window.location.pathname}?section_id=${id}&${searchParams}`;
      try {
        const response = await fetch(url);
        if (response.ok === false) {
          throw new Error(`Fetch "${id}" section response was not OK`);
        }
        const html = await response.text();
        const newDocument = new DOMParser().parseFromString(html, 'text/html');

        resultsGrid.innerHTML = newDocument.querySelector(selectors.resultsGrid).innerHTML;

        // Update the results count
        const resultsCount = container.querySelector(selectors.resultsCount);
        const newResultsCount = newDocument.querySelector(selectors.resultsCount);
        if (resultsCount && newResultsCount) {
          resultsCount.innerHTML = newResultsCount.innerHTML;
        }

        // Replace each filter content separately
        if (filterContent) {
          filterContent.forEach((content, index) => {
            // Don't reload the current filter because it causes bugs
            if (currentFilter && content === currentFilter) {
              return;
            }
            const filterContentNew = newDocument.querySelectorAll(selectors.filterContent);
            content.innerHTML = filterContentNew[index].innerHTML;
          });
        }

        if (filterFooter) {
          filterFooter.innerHTML = newDocument.querySelector(selectors.filterFooter).innerHTML;
        }

        if (filterTopbar) {
          filterTopbar.innerHTML = newDocument.querySelector(selectors.filterTopbar).innerHTML;
        }

        if (pagination) {
          const paginationNew = newDocument.querySelectorAll(selectors.pagination);
          pagination.forEach((item, index) => {
            item.innerHTML = paginationNew[index] ? paginationNew[index].innerHTML : '';
          });
          initPagination(id);
        }

        history.replaceState(
          {searchParams},
          '',
          `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`,
        );

        // Reset the updating flag before re-initialization so state can be restored
        rangeSliderState.isUpdating = false;

        initPriceRangeSlider();

        // Update sizes attr for images if alt layout is selected
        if (selectedLayoutView === 'alt') {
          updateImageSizesAttr();
        }

        // Dispatch event that results grid has been updated with new content
        document.dispatchEvent(new CustomEvent('results-grid:updated'));

        // Wishlist Plus (Swym) - custom event dispatched to initialise button for new products loaded
        document.dispatchEvent(new CustomEvent('swym:products-loaded'));

        // Only reinitialize dropdowns if the navbar content has changed
        const navbarButtons = container.querySelectorAll('[data-navbar-filter-toggle]');
        const hasUninitializedButtons = Array.from(navbarButtons).some(
          (button) => !button.hasAttribute('data-dropdown-initialized'),
        );

        if (hasUninitializedButtons) {
          initNavbarFilterDropdowns();
        }

        // Re-initialize drawer checkbox listeners after AJAX update
        initDrawerCheckboxListeners();
      } catch (err) {
        window.console.error(err);
      }
    };

    function updateImageSizesAttr() {
      const resultsGridImages = resultsGrid.querySelectorAll('img');

      resultsGridImages.forEach((image) => {
        image.sizes = resultsGrid.dataset.imageSizesAlt;
      });
    }

    const toggleSortByModal = () => {
      if (!sortByToggle || !sortByModal) return;
      sortByModal.style.height = sortByIsExpanded ? '0' : `${sortByModal.scrollHeight}px`;
      sortByModal.classList.toggle(classes.sortByActive);
      sortByIsExpanded = !sortByIsExpanded;
      sortByToggle.ariaExpanded = sortByIsExpanded;
      document.body.classList.add(classes.bodySortActive);
    };

    const handleNavbarFormSubmit = async (event) => {
      event.preventDefault();

      // Get the form element - whether the event target is a direct form element or nested
      const form = event.target.closest('form') || resultsNavbar;

      // Create the form data and deduplicate parameters
      const formData = new FormData(form);
      const deduplicatedFormData = deduplicateFormData(formData);

      const searchParams = new URLSearchParams(deduplicatedFormData);
      const keyValuePairsToAdd = [];
      const keyValuePairsToRemove = [{key: 'sort_by', value: defaultSortBy}, {key: 'products-per-page'}];

      // Only remove price range values if they are at their default/full range values
      const filterPriceRangeSlider = container.querySelector(selectors.filterPriceRangeSlider);
      const filterPriceRangeMin = container.querySelector(selectors.filterPriceRangeMin);
      const filterPriceRangeMax = container.querySelector(selectors.filterPriceRangeMax);

      if (filterPriceRangeSlider && filterPriceRangeMin && filterPriceRangeMax) {
        const sliderMin = parseFloat(filterPriceRangeSlider.getAttribute('min')) || 0;
        const sliderMax = parseFloat(filterPriceRangeSlider.getAttribute('max')) || 100;
        const currentMin = parseFloat(filterPriceRangeMin.value);
        const currentMax = parseFloat(filterPriceRangeMax.value);

        // Only remove if the values are at the full range (no filtering applied)
        if (currentMin <= sliderMin) {
          keyValuePairsToRemove.push({key: filterPriceRangeMin.name, value: currentMin});
        }
        if (currentMax >= sliderMax) {
          keyValuePairsToRemove.push({key: filterPriceRangeMax.name, value: currentMax});
        }
      }

      if (searchParams.has('filter.v.m.filter.size')) {
        keyValuePairsToAdd.push({key: 'filter.v.availability', value: '1'});
      } else {
        keyValuePairsToRemove.push({key: 'filter.v.availability'});
      }

      const filteredSearchParams = filterSearchParams(searchParams, keyValuePairsToAdd, keyValuePairsToRemove);
      const currentFilter = event.target.closest(selectors.filterContent);
      if (event.target.name === 'products-per-page') {
        const rootUrl = window.theme.locale && !window.theme.locale.primary ? window.theme.locale.rootUrl : '';
        const url = `${rootUrl}/cart/update.js`;
        const options = {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({attributes: {collection_products_per_page: event.target.value}}),
        };
        try {
          await fetch(url, options);
        } catch (err) {
          window.console.error(err);
        }
      }
      renderPage(filteredSearchParams, currentFilter);

      // Close sort by modal if open
      if (sortByIsExpanded) {
        toggleSortByModal();
      }
    };

    const debouncedNavbarFormSubmit = debounce((event) => {
      handleNavbarFormSubmit(event);
    }, 500);

    resultsNavbar.addEventListener('input', debouncedNavbarFormSubmit);
    resultsNavbar.addEventListener('submit', handleNavbarFormSubmit);

    // Filters
    if (filterDrawer) {
      // Toggle filters drawer
      const handleFilterToggles = (event) => {
        const filterToggle = event.target.closest(selectors.filterToggle);
        if (!filterToggle) return;

        if (filterDrawer.classList.contains(classes.isHidden)) {
          filterDrawer.classList.remove(classes.isHidden);
          filterDrawer.classList.add(classes.isVisible);
          filterDrawer.setAttribute('aria-hidden', false);
          document.body.classList.add(classes.filterIsVisible);
        } else {
          filterDrawer.classList.add(classes.isHidden);
          filterDrawer.classList.remove(classes.isVisible);
          filterDrawer.setAttribute('aria-hidden', true);
          document.body.classList.remove(classes.filterIsVisible);
        }
      };

      // Use document to catch all clicks on filter toggle buttons
      document.addEventListener('click', function (event) {
        const filterToggle = event.target.closest(selectors.filterToggle);
        if (filterToggle) {
          handleFilterToggles(event);
        }
      });

      // Clear filters links
      const handleClearFiltersLinkClick = (event) => {
        const filterClearLink = event.target.closest(selectors.filterClearLink);
        if (!filterClearLink) return;

        event.preventDefault();
        const searchParams = filterClearLink.href.split('?')[1] ?? '';
        renderPage(searchParams);
      };
      filterDrawer.addEventListener('click', handleClearFiltersLinkClick);
    }

    if (filterContent) {
      // Price range filter
      initPriceRangeSlider();
    }

    // Layout switcher
    const layoutSwitchButtons = container.querySelectorAll(selectors.layoutSwitchButton);
    let imgSizesUpdated = false;

    const handleLayoutSwitch = (target) => {
      const layout = target.dataset.resultsLayoutSwitch;
      const classesToRemove = Array.from(resultsGrid.classList).filter((value) => value.includes('results-grid--'));
      resultsGrid.classList.add(`results-grid--${layout}`);
      resultsGrid.classList.remove(...classesToRemove);

      // Update sizes attr for images if alt layout is selected and sizes have not been updated already
      if (layout === 'alt' && !imgSizesUpdated) {
        updateImageSizesAttr();
        imgSizesUpdated = true;
      }

      layoutSwitchButtons.forEach((button) => {
        button.setAttribute('aria-pressed', 'false');
        button.removeAttribute('disabled');
        button.classList.remove(classes.layoutSwitchSelected);
      });
      target.setAttribute('disabled', 'disabled');
      target.setAttribute('aria-pressed', 'true');
      target.classList.add(classes.layoutSwitchSelected);
      selectedLayoutView = layout;
      window.sessionStorage.setItem('resultsLayoutView', layout);
    };

    if (selectedLayoutView === 'alt') {
      const layoutSwitchAltButton = [...layoutSwitchButtons].find(
        (button) => button.dataset.resultsLayoutSwitch === 'alt',
      );
      if (layoutSwitchAltButton) {
        handleLayoutSwitch(layoutSwitchAltButton);
      }
    }

    layoutSwitchButtons.forEach((layoutSwitchButton) => {
      layoutSwitchButton.addEventListener('click', (event) => handleLayoutSwitch(event.target));
    });

    // Sort by modal toggle
    sortByToggle?.addEventListener('click', (event) => {
      event.preventDefault();
      toggleSortByModal();
    });
  }

  function initPriceRangeSlider() {
    const filterPriceRangeSlider = container.querySelector(selectors.filterPriceRangeSlider);
    const filterPriceRangeMin = container.querySelector(selectors.filterPriceRangeMin);
    const filterPriceRangeMax = container.querySelector(selectors.filterPriceRangeMax);
    if (!filterPriceRangeSlider || !filterPriceRangeMin || !filterPriceRangeMax) return;

    // Get the slider's min/max range from attributes
    const sliderMin = parseFloat(filterPriceRangeSlider.getAttribute('min')) || 0;
    const sliderMax = parseFloat(filterPriceRangeSlider.getAttribute('max')) || 100;

    // Flag to prevent feedback loops during programmatic updates
    let isUpdatingProgrammatically = false;

    // Initialize state from current values if not already set
    if (rangeSliderState.valueMin === null || rangeSliderState.valueMax === null) {
      rangeSliderState.valueMin = parseFloat(filterPriceRangeSlider.getAttribute('valueMin')) || sliderMin;
      rangeSliderState.valueMax = parseFloat(filterPriceRangeSlider.getAttribute('valueMax')) || sliderMax;
    }

    // Restore saved state if available and within valid range
    if (rangeSliderState.valueMin !== null && rangeSliderState.valueMax !== null && !rangeSliderState.isUpdating) {
      // Ensure saved values are within the slider's range
      const savedMin = Math.max(sliderMin, Math.min(sliderMax, rangeSliderState.valueMin));
      const savedMax = Math.max(sliderMin, Math.min(sliderMax, rangeSliderState.valueMax));

      if (savedMin >= sliderMin && savedMax <= sliderMax) {
        isUpdatingProgrammatically = true;
        filterPriceRangeSlider.valueMin = savedMin;
        filterPriceRangeSlider.valueMax = savedMax;
        filterPriceRangeMin.value = savedMin;
        filterPriceRangeMax.value = savedMax;
        isUpdatingProgrammatically = false;
      }
    }

    filterPriceRangeSlider.addEventListener('input', (event) => {
      if (isUpdatingProgrammatically) return;

      const {valueMin, valueMax} = event.detail;

      // Update global state
      rangeSliderState.valueMin = valueMin;
      rangeSliderState.valueMax = valueMax;

      // Update input fields without triggering their events
      isUpdatingProgrammatically = true;
      filterPriceRangeMin.value = valueMin;
      filterPriceRangeMax.value = valueMax;
      isUpdatingProgrammatically = false;
    });

    filterPriceRangeSlider.addEventListener('change', (event) => {
      const {valueMin, valueMax} = event.detail;

      // Store the current slider values before submitting
      rangeSliderState.valueMin = valueMin;
      rangeSliderState.valueMax = valueMax;
      rangeSliderState.isUpdating = true;

      // Update the input values
      isUpdatingProgrammatically = true;
      filterPriceRangeMin.value = valueMin;
      filterPriceRangeMax.value = valueMax;
      isUpdatingProgrammatically = false;

      if (resultsNavbar.requestSubmit) {
        return resultsNavbar.requestSubmit();
      }

      const submitButton = resultsNavbar.querySelector('[type="submit"]');
      if (!submitButton) return;

      submitButton.click();
    });

    filterPriceRangeMin.addEventListener(
      'input',
      debounce(() => {
        if (isUpdatingProgrammatically) return;

        const value = parseFloat(filterPriceRangeMin.value);
        if (isNaN(value)) return;

        rangeSliderState.valueMin = value;

        isUpdatingProgrammatically = true;
        filterPriceRangeSlider.valueMin = value;
        isUpdatingProgrammatically = false;
      }, 500),
    );

    filterPriceRangeMax.addEventListener(
      'input',
      debounce(() => {
        if (isUpdatingProgrammatically) return;

        const value = parseFloat(filterPriceRangeMax.value);
        if (isNaN(value)) return;

        rangeSliderState.valueMax = value;

        isUpdatingProgrammatically = true;
        filterPriceRangeSlider.valueMax = value;
        isUpdatingProgrammatically = false;
      }, 500),
    );
  }

  // Initialize event handlers for navbar filter dropdowns
  function initNavbarFilterDropdowns() {
    const navbarFilterDropdowns = container.querySelectorAll('.results-navbar__filter-dropdown');
    const navbarFilterButtons = container.querySelectorAll('[data-navbar-filter-toggle]');

    if (navbarFilterButtons.length === 0) {
      return;
    }

    // Detect if device supports touch
    const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;

    // Handle dropdown toggle functionality
    navbarFilterButtons.forEach((button, index) => {
      // Remove any existing event listeners to prevent duplicates
      button.removeEventListener('click', handleNavbarFilterToggle);
      button.removeEventListener('touchstart', handleNavbarFilterToggle);

      // Add a data attribute to track if we've initialized this button
      if (!button.hasAttribute('data-dropdown-initialized')) {
        button.setAttribute('data-dropdown-initialized', 'true');
      }

      if (isTouchDevice) {
        // Add event listeners with proper mobile handling for touch devices
        let touchStarted = false;

        button.addEventListener(
          'touchstart',
          (event) => {
            touchStarted = true;
            handleNavbarFilterToggle(event);
            // Reset the flag after a short delay
            setTimeout(() => {
              touchStarted = false;
            }, 300);
          },
          {passive: true},
        );

        button.addEventListener('click', (event) => {
          // Only handle click if it wasn't preceded by a touchstart
          if (!touchStarted) {
            handleNavbarFilterToggle(event);
          }
        });
      } else {
        // For non-touch devices (desktop), use simple click handling
        button.addEventListener('click', (event) => {
          handleNavbarFilterToggle(event);
        });
      }
    });

    // Handle checkbox changes in dropdowns
    navbarFilterDropdowns.forEach((dropdown) => {
      const checkboxes = dropdown.querySelectorAll('input[type="checkbox"]');

      checkboxes.forEach((checkbox) => {
        // First remove any existing event listeners to prevent duplicates
        checkbox.removeEventListener('change', handleNavbarCheckboxChange);

        // Then add the new event listener
        checkbox.addEventListener('change', handleNavbarCheckboxChange);
      });
    });

    // Also add listeners to drawer checkboxes to sync back to navbar
    initDrawerCheckboxListeners();

    // Close dropdowns when clicking outside
    document.removeEventListener('click', handleOutsideClick);
    document.removeEventListener('touchstart', handleOutsideClick);

    if (isTouchDevice) {
      let outsideTouchStarted = false;

      document.addEventListener(
        'touchstart',
        (event) => {
          outsideTouchStarted = true;
          handleOutsideClick(event);
          setTimeout(() => {
            outsideTouchStarted = false;
          }, 300);
        },
        {passive: true},
      );

      document.addEventListener('click', (event) => {
        if (!outsideTouchStarted) {
          handleOutsideClick(event);
        }
      });
    } else {
      // For non-touch devices, use simple click handling
      document.addEventListener('click', handleOutsideClick);
    }
  }

  // Re-initialize dropdowns if needed (called after DOM changes)
  function reinitializeDropdownsIfNeeded() {
    const buttons = container.querySelectorAll('[data-navbar-filter-toggle]');
    let needsReinit = false;

    buttons.forEach((button) => {
      if (!button.hasAttribute('data-dropdown-initialized')) {
        needsReinit = true;
      }
    });

    if (needsReinit) {
      initNavbarFilterDropdowns();
    }
  }

  // Handle navbar filter toggle button clicks
  function handleNavbarFilterToggle(event) {
    event.preventDefault();
    event.stopPropagation();

    const button = event.currentTarget;
    const filterId = button.getAttribute('data-navbar-filter-toggle');
    const dropdown = container.querySelector(`[data-navbar-filter-dropdown="${filterId}"]`);

    if (!dropdown) {
      return;
    }

    // Check if this dropdown is currently open
    const isCurrentlyOpen = dropdown.classList.contains('is-active');

    // Close all dropdowns in this container first
    const allButtons = container.querySelectorAll('[data-navbar-filter-toggle]');

    allButtons.forEach((otherButton) => {
      otherButton.setAttribute('aria-expanded', 'false');
      const otherId = otherButton.getAttribute('data-navbar-filter-toggle');
      const otherDropdown = container.querySelector(`[data-navbar-filter-dropdown="${otherId}"]`);
      if (otherDropdown) {
        otherDropdown.classList.remove('is-active');
      }
    });

    // If the clicked dropdown wasn't open, open it
    if (!isCurrentlyOpen) {
      button.setAttribute('aria-expanded', 'true');
      dropdown.classList.add('is-active');
    }
  }

  // Handle clicks outside dropdowns to close them
  function handleOutsideClick(event) {
    // Don't handle if the click is on a dropdown toggle button or inside a dropdown
    // This prevents the dropdown from closing immediately after opening
    if (
      event.target.closest('[data-navbar-filter-toggle]') ||
      event.target.closest('.results-navbar__filter-dropdown')
    ) {
      return;
    }

    const navbarFilterButtons = container.querySelectorAll('[data-navbar-filter-toggle]');
    navbarFilterButtons.forEach((button) => {
      button.setAttribute('aria-expanded', 'false');
      const filterId = button.getAttribute('data-navbar-filter-toggle');
      const dropdown = container.querySelector(`[data-navbar-filter-dropdown="${filterId}"]`);
      if (dropdown) {
        dropdown.classList.remove('is-active');
      }
    });
  }

  // Handle checkbox changes in navbar filter dropdowns
  function handleNavbarCheckboxChange(event) {
    const navbarCheckbox = event.target;

    // Find the equivalent checkbox in the drawer filters
    const filterName = navbarCheckbox.name;
    const filterValue = navbarCheckbox.value;

    // Find the matching drawer checkbox with the same name and value
    const drawerCheckbox = container.querySelector(
      `${selectors.filterDrawer} input[name="${filterName}"][value="${filterValue}"]`,
    );

    if (drawerCheckbox) {
      // Update the drawer checkbox to match the navbar checkbox
      drawerCheckbox.checked = navbarCheckbox.checked;

      // Trigger a change event on the drawer checkbox
      const changeEvent = new Event('change', {bubbles: true});
      drawerCheckbox.dispatchEvent(changeEvent);

      // Prevent default to avoid running the navbar submission logic
      event.preventDefault();
    } else {
      console.warn(`Could not find matching drawer checkbox for ${filterName}=${filterValue}`);

      // If no drawer checkbox found, fall back to form submission
      const form = resultsNavbar;
      const formData = new FormData(form);
      const deduplicatedFormData = deduplicateFormData(formData);

      const searchParams = new URLSearchParams(deduplicatedFormData);
      const keyValuePairsToAdd = [];
      const keyValuePairsToRemove = [{key: 'sort_by', value: defaultSortBy}, {key: 'products-per-page'}];

      // Only remove price range values if they are at their default/full range values
      const filterPriceRangeSlider = container.querySelector(selectors.filterPriceRangeSlider);
      const filterPriceRangeMin = container.querySelector(selectors.filterPriceRangeMin);
      const filterPriceRangeMax = container.querySelector(selectors.filterPriceRangeMax);

      if (filterPriceRangeSlider && filterPriceRangeMin && filterPriceRangeMax) {
        const sliderMin = parseFloat(filterPriceRangeSlider.getAttribute('min')) || 0;
        const sliderMax = parseFloat(filterPriceRangeSlider.getAttribute('max')) || 100;
        const currentMin = parseFloat(filterPriceRangeMin.value);
        const currentMax = parseFloat(filterPriceRangeMax.value);

        // Only remove if the values are at the full range (no filtering applied)
        if (currentMin <= sliderMin) {
          keyValuePairsToRemove.push({key: filterPriceRangeMin.name, value: currentMin});
        }
        if (currentMax >= sliderMax) {
          keyValuePairsToRemove.push({key: filterPriceRangeMax.name, value: currentMax});
        }
      }

      if (searchParams.has('filter.v.m.filter.size')) {
        keyValuePairsToAdd.push({key: 'filter.v.availability', value: '1'});
      } else {
        keyValuePairsToRemove.push({key: 'filter.v.availability'});
      }

      const filteredSearchParams = filterSearchParams(searchParams, keyValuePairsToAdd, keyValuePairsToRemove);
      renderPage(filteredSearchParams, null);
    }
  }

  // Handle checkbox changes in drawer filters (sync back to navbar)
  function handleDrawerCheckboxChange(event) {
    const drawerCheckbox = event.target;

    // Find the equivalent checkbox in the navbar filters
    const filterName = drawerCheckbox.name;
    const filterValue = drawerCheckbox.value;

    // Find the matching navbar checkbox with the same name and value
    const navbarCheckbox = container.querySelector(
      `[data-navbar-filter-dropdown] input[name="${filterName}"][value="${filterValue}"]`,
    );

    if (navbarCheckbox) {
      // Update the navbar checkbox to match the drawer checkbox
      // Use a flag to prevent infinite loops
      if (navbarCheckbox.checked !== drawerCheckbox.checked) {
        navbarCheckbox.checked = drawerCheckbox.checked;
      }
    } else {
      console.warn(`Could not find matching navbar checkbox for ${filterName}=${filterValue}`);
    }
  }

  // Add mutation observer to watch for DOM changes that might affect our dropdowns
  function observeDropdownChanges() {
    const observer = new MutationObserver((mutations) => {
      let shouldReinit = false;

      mutations.forEach((mutation) => {
        if (mutation.type === 'childList') {
          // Check if any navbar filter buttons were added or removed
          const addedNodes = Array.from(mutation.addedNodes);
          const removedNodes = Array.from(mutation.removedNodes);

          const hasNavbarChanges = [...addedNodes, ...removedNodes].some((node) => {
            if (node.nodeType === Node.ELEMENT_NODE) {
              return node.matches('[data-navbar-filter-toggle]') || node.querySelector('[data-navbar-filter-toggle]');
            }
            return false;
          });

          if (hasNavbarChanges) {
            shouldReinit = true;
          }
        }
      });

      if (shouldReinit) {
        setTimeout(() => {
          reinitializeDropdownsIfNeeded();
        }, 100);
      }
    });

    observer.observe(container, {
      childList: true,
      subtree: true,
    });

    return observer;
  }

  // Initialize drawer checkbox listeners for syncing back to navbar
  function initDrawerCheckboxListeners() {
    const filterDrawer = container.querySelector(selectors.filterDrawer);
    if (!filterDrawer) return;

    const drawerCheckboxes = filterDrawer.querySelectorAll('input[type="checkbox"]');

    drawerCheckboxes.forEach((drawerCheckbox) => {
      // Remove existing listeners to prevent duplicates
      drawerCheckbox.removeEventListener('change', handleDrawerCheckboxChange);

      // Add listener for drawer to navbar sync
      drawerCheckbox.addEventListener('change', handleDrawerCheckboxChange);
    });

    // Also sync price range inputs between drawer and navbar
    initPriceRangeSynchronization();
  }

  // Initialize price range synchronization between drawer and navbar
  function initPriceRangeSynchronization() {
    const filterDrawer = container.querySelector(selectors.filterDrawer);
    if (!filterDrawer) return;

    // Find drawer price inputs (in price-range component)
    const drawerPriceMin = filterDrawer.querySelector('input[name="filter.v.price.gte"]');
    const drawerPriceMax = filterDrawer.querySelector('input[name="filter.v.price.lte"]');

    // Find navbar price elements
    const navbarPriceSlider = container.querySelector(selectors.filterPriceRangeSlider);
    const navbarPriceMin = container.querySelector(selectors.filterPriceRangeMin);
    const navbarPriceMax = container.querySelector(selectors.filterPriceRangeMax);

    if (!drawerPriceMin || !drawerPriceMax || !navbarPriceSlider || !navbarPriceMin || !navbarPriceMax) {
      return;
    }

    // Sync from drawer to navbar
    const syncDrawerToNavbar = () => {
      const minValue = parseFloat(drawerPriceMin.value) || 0;
      const maxValue = parseFloat(drawerPriceMax.value) || 100;

      // Update navbar elements
      navbarPriceMin.value = minValue;
      navbarPriceMax.value = maxValue;
      navbarPriceSlider.valueMin = minValue;
      navbarPriceSlider.valueMax = maxValue;

      // Update global state
      rangeSliderState.valueMin = minValue;
      rangeSliderState.valueMax = maxValue;
    };

    // Sync from navbar to drawer
    const syncNavbarToDrawer = () => {
      const minValue = parseFloat(navbarPriceMin.value) || 0;
      const maxValue = parseFloat(navbarPriceMax.value) || 100;

      // Update drawer elements
      drawerPriceMin.value = minValue;
      drawerPriceMax.value = maxValue;
    };

    // Add event listeners for drawer price inputs
    drawerPriceMin.removeEventListener('input', syncDrawerToNavbar);
    drawerPriceMax.removeEventListener('input', syncDrawerToNavbar);
    drawerPriceMin.addEventListener('input', syncDrawerToNavbar);
    drawerPriceMax.addEventListener('input', syncDrawerToNavbar);

    // Add event listeners for navbar price inputs
    navbarPriceMin.removeEventListener('input', syncNavbarToDrawer);
    navbarPriceMax.removeEventListener('input', syncNavbarToDrawer);
    navbarPriceMin.addEventListener('input', syncNavbarToDrawer);
    navbarPriceMax.addEventListener('input', syncNavbarToDrawer);

    // Initial sync to ensure they match on page load
    syncDrawerToNavbar();
  }

  // Remove the duplicate init function - keep only the return statement
  return {
    // Expose reinitialize function for manual calls if needed
    reinitializeDropdowns: initNavbarFilterDropdowns,
  };
};

export default ResultsFilterSort;
